import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable";
import { cx } from "@zenchef/styled-system/css";
import React, {
  ReactElement,
  useCallback,
  createContext,
  useMemo,
  useEffect,
} from "react";
import { Box, BoxProps } from "@zenchef/styled-system/jsx";
import { aromaDraggableContainer } from "@zenchef/styled-system/recipes";
import DraggableItem, {
  DraggableItemProps,
  DraggableItemData,
} from "./DraggableItem";
import useFlipDraggableAnimation from "./useFlipDraggableAnimation";
import useIsTabletScreen from "../../utils/useIsTabletScreen";

type DraggableItemComponent = ReactElement<
  DraggableItemProps,
  typeof DraggableItem
>;

export interface SortEndData {
  source: {
    id: DraggableItemData["id"];
    index: number;
  };
  destination: {
    id: DraggableItemData["id"];
    index: number;
  };
}
export const DraggableContext = createContext<{
  moveItem: (id: DraggableItemData["id"], direction: "up" | "down") => void;
  flow: "vertical" | "horizontal";
} | null>(null);

interface DraggableContainerProps
  extends Omit<BoxProps, "onDragStart" | "onDragEnd"> {
  children: React.ReactNode;
  flow?: "vertical" | "horizontal";
  onSortEnd: (sortEndData: SortEndData) => void;
  onDragStart?: (value: boolean) => void;
  onDragEnd?: (value: boolean) => void;
}

const activationConstraint = {
  activationConstraint: {
    delay: 200,
    tolerance: 5,
  },
};

const DraggableContainer = ({
  children,
  className,
  onSortEnd,
  onDragStart,
  onDragEnd,
  flow = "vertical",
  ...props
}: DraggableContainerProps) => {
  const containerRef = useFlipDraggableAnimation();
  const isTabletScreen = useIsTabletScreen();
  const sensors = isTabletScreen
    ? useSensors(useSensor(TouchSensor, activationConstraint))
    : useSensors(useSensor(PointerSensor, activationConstraint));

  const childrenArray = React.Children.toArray(
    children,
  ) as DraggableItemComponent[];

  const itemIds = useMemo(
    () => childrenArray.map((child) => child.props.id),
    [childrenArray],
  );

  const [activeId, setActiveId] = React.useState<string | number | null>(null);
  const [activeItemStyle, setActiveItemStyle] = React.useState<
    React.CSSProperties | undefined
  >(undefined);

  // Ref map for draggable items
  const itemRefs = React.useRef<Record<string | number, HTMLDivElement | null>>(
    {},
  );
  const setItemRef = (id: string | number) => (node: HTMLDivElement | null) => {
    itemRefs.current[id] = node;
  };

  const moveItem = useCallback(
    (id: DraggableItemData["id"], direction: "up" | "down") => {
      const indexOfSource = childrenArray.findIndex(
        (child) => child.props.id === id,
      );
      const indexOfDestination = indexOfSource + (direction === "up" ? -1 : 1);
      if (
        indexOfDestination < 0 ||
        indexOfDestination >= childrenArray.length
      ) {
        return;
      }
      onSortEnd({
        source: {
          id: id,
          index: indexOfSource,
        },
        destination: {
          id: childrenArray[indexOfDestination].props.id,
          index: indexOfDestination,
        },
      });
    },
    [childrenArray, onSortEnd],
  );

  const handleDragStart = React.useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id);
    const node = itemRefs.current[event.active.id];
    onDragStart?.(true);

    if (node) {
      setActiveItemStyle({
        width: node.offsetWidth,
        height: node.offsetHeight,
      });
    }
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveId(null);
      setActiveItemStyle(undefined);
      onDragEnd?.(true);

      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIndex = childrenArray.findIndex(
        (child) => child.props.id === active.id,
      );
      const newIndex = childrenArray.findIndex(
        (child) => child.props.id === over.id,
      );
      if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return;
      onSortEnd({
        source: {
          id: active.id,
          index: oldIndex,
        },
        destination: {
          id: over.id,
          index: newIndex,
        },
      });
    },
    [childrenArray, onSortEnd],
  );

  const classes = aromaDraggableContainer();
  const activeItem = childrenArray.find((child) => child.props.id === activeId);

  // Clone children to inject forwardedRef
  const childrenWithRefs = useMemo(
    () =>
      childrenArray.map((child) =>
        React.cloneElement(child, {
          forwardedRef: setItemRef(child.props.id),
        }),
      ),
    [childrenArray],
  );

  // Cleanup refs for unmounted items (optional, for dynamic lists)
  useEffect(() => {
    const currentIds = new Set(childrenArray.map((child) => child.props.id));
    Object.keys(itemRefs.current).forEach((id) => {
      if (!currentIds.has(id)) {
        delete itemRefs.current[id];
      }
    });
  }, [childrenArray]);

  return (
    <DraggableContext.Provider value={{ moveItem, flow }}>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={itemIds}
          strategy={
            flow === "vertical"
              ? verticalListSortingStrategy
              : horizontalListSortingStrategy
          }
        >
          <Box className={cx(classes, className)} {...props} ref={containerRef}>
            {childrenWithRefs}
          </Box>
          <DragOverlay>
            {activeItem && !activeItem.props.disabled
              ? React.cloneElement(activeItem, {
                  active: true,
                  style: activeItemStyle,
                })
              : null}
          </DragOverlay>
        </SortableContext>
      </DndContext>
    </DraggableContext.Provider>
  );
};

DraggableContainer.Item = DraggableItem;

export default DraggableContainer;
